<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none grid-header">
			<div class="shipping-header-actions">
				<v-btn
					depressed
					rounded
					color="success"
					@click="newShippingMethod()"
					:disabled="activeEdit !== null"
				>
					New Shipping Method
				</v-btn>
			</div>
		</v-col>

		<v-col cols="12" class="flex-auto">
			<v-row>
				<v-col cols="7" class="flex-auto">
					<div class="shipping-table">
						<div v-if="initData" class="loading-overlay">
							<div class="loading-dot">&nbsp;</div>
							<div class="loading-dot">&nbsp;</div>
							<div class="loading-dot">&nbsp;</div>
							<div class="loading-dot">&nbsp;</div>
							<div class="loading-dot">&nbsp;</div>
						</div>
						<div class="flex-table" role="table-head">
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell" role="column-header">
									<v-btn
										v-if="deleteMethods.length > 0"
										icon
										small
										color="primary"
										@click="deleteWarning = !deleteWarning"
										><v-icon small>mdi-delete</v-icon>
									</v-btn>
								</div>
								<div class="flex-table-cell" role="column-header">Shipping Method</div>
								<div class="flex-table-cell" role="column-header">Carrier</div>
								<div class="flex-table-cell" role="column-header">Service</div>
								<div class="flex-table-cell" role="column-header">Package Type</div>
								<div class="flex-table-cell" role="column-header">Max Weight</div>
								<div class="flex-table-cell" role="column-header">Calculation Code</div>
								<div class="flex-table-cell" role="column-header">&nbsp;</div>
								<div class="flex-table-cell" role="column-header">&nbsp;</div>
							</div>
						</div>
						<div
							class="flex-table"
							role="table-body"
							@drop="onDrop()"
							@dragover.prevent="startSort ? sortItems($event) : null"
						>
							<div
								v-for="(data, i) in shippingMethods"
								:key="i"
								class="flex-table-row"
								role="row-group"
								:style="`order: ${data.sequence}`"
								:draggable="startSort"
							>
								<template v-if="data == activeEdit">
									<div class="flex-table-cell" role="cell">
										<v-icon
											small
											v-text="'mdi-arrow-split-horizontal'"
											@mousedown="triggerSorting($event, data)"
										></v-icon>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.shippingMethodName"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-select
												v-model="data.carrierId"
												:items="carriers"
												item-text="displayName"
												item-value="carrierId"
												dense
												outlined
												hide-details="auto"
												@change="onCarrierChange(data)"
											>
												<template v-slot:item="{ item }">
													<img
														v-if="getCarrierLogo(item.carrierCode)"
														:src="getCarrierLogo(item.carrierCode)"
														:alt="item.displayName"
														class="carrier-logo-dropdown mr-2"
														style="height: 16px !important; width: auto !important; max-width: 40px !important;"
													/>
													<v-icon v-else-if="getCarrierIcon(item.carrierCode)" class="mr-2">
														{{ getCarrierIcon(item.carrierCode) }}
													</v-icon>
													{{ item.displayName }}
												</template>
											</v-select>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-select
												v-model="data.serviceCode"
												:items="getCarrierServices(data.carrierId)"
												item-text="name"
												item-value="serviceCode"
												dense
												outlined
												hide-details="auto"
												:disabled="!data.carrierId"
											>
												<template v-slot:item="{ item }">
													<v-tooltip
														right
														:disabled="!getUpsServiceDescription(item.serviceCode, data.carrierId)"
														max-width="350"
														content-class="ups-service-tooltip"
													>
														<template v-slot:activator="{ on, attrs }">
															<span
																class="d-flex align-center"
																style="width: 100%;"
																v-bind="attrs"
																v-on="on"
															>
																{{ item.name }}
																<v-icon
																	v-if="getUpsServiceDescription(item.serviceCode, data.carrierId)"
																	x-small
																	color="info"
																	class="ml-1"
																>
																	mdi-information-outline
																</v-icon>
																<v-tooltip right v-if="getServiceWarning(item.serviceCode)">
																	<template v-slot:activator="{ on: warnOn, attrs: warnAttrs }">
																		<v-icon
																			small
																			color="warning"
																			class="ml-1"
																			v-bind="warnAttrs"
																			v-on="warnOn"
																		>
																			mdi-alert-circle-outline
																		</v-icon>
																	</template>
																	<span style="max-width: 300px; display: block;">
																		{{ getServiceWarning(item.serviceCode) }}
																	</span>
																</v-tooltip>
															</span>
														</template>
														<div class="ups-tooltip-content">
															<div class="ups-tooltip-title">{{ item.name }}</div>
															<div class="ups-tooltip-desc">{{ getUpsServiceDescription(item.serviceCode, data.carrierId) }}</div>
														</div>
													</v-tooltip>
												</template>
											</v-select>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-select
												v-model="data.packageType"
												:items="getCarrierPackages(data.carrierId)"
												item-text="name"
												item-value="packageCode"
												dense
												outlined
												hide-details="auto"
												:disabled="!data.carrierId"
											></v-select>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model.number="data.maxWeight"
												type="number"
												suffix="lbs"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<div class="form-input">
											<v-select
												v-model="data.shippingCategoryId"
												:items="shippingCategories"
												item-text="category"
												item-value="shippingCategoryId"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</div>
									<div class="flex-table-cell" role="cell">
										<v-btn
											icon
											color="primary"
											@click="
												data.shippingMethodId == null
													? removeShippingMethod()
													: (activeEdit = null);
												$apollo.queries.shippingMethods.refetch();
											"
											><v-icon small>mdi-close</v-icon>
										</v-btn>
									</div>
									<div class="flex-table-cell" role="cell">
										<v-btn icon color="primary" @click="saveShippingMethod()"
											><v-icon small>mdi-check</v-icon>
										</v-btn>
									</div>
								</template>
								<template v-else>
									<div class="flex-table-cell" role="cell">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.selected"
											color="accent"
											hide-details="auto"
											@change="selectShippingMethods(data)"
										></v-checkbox>
									</div>
									<div class="flex-table-cell" role="cell">{{ data.shippingMethodName }}</div>
									<div class="flex-table-cell carrier-cell" role="cell">
										<img
											v-if="getCarrierLogo(getCarrierCodeFromId(data.carrierId) || data.carrierCode)"
											:src="getCarrierLogo(getCarrierCodeFromId(data.carrierId) || data.carrierCode)"
											:alt="getCarrierName(data.carrierId, data.carrierCode)"
											class="carrier-logo"
											style="height: 20px !important; width: auto !important; max-width: 60px !important;"
										/>
										<v-icon v-else-if="getCarrierIcon(getCarrierCodeFromId(data.carrierId) || data.carrierCode)" small class="mr-1">
											{{ getCarrierIcon(getCarrierCodeFromId(data.carrierId) || data.carrierCode) }}
										</v-icon>
										<span>{{ getCarrierName(data.carrierId, data.carrierCode) }}</span>
									</div>
									<div class="flex-table-cell" role="cell">
										<span class="d-flex align-center">
											{{ getServiceName(data.carrierId || data.carrierCode, data.serviceCode) }}
											<v-tooltip right v-if="getServiceWarning(data.serviceCode)">
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														small
														color="warning"
														class="ml-1"
														v-bind="attrs"
														v-on="on"
													>
														mdi-alert-circle-outline
													</v-icon>
												</template>
												<span style="max-width: 300px; display: block;">
													{{ getServiceWarning(data.serviceCode) }}
												</span>
											</v-tooltip>
										</span>
									</div>
									<div class="flex-table-cell" role="cell">
										{{ getPackageName(data.carrierId || data.carrierCode, data.packageType) }}
									</div>
									<div class="flex-table-cell" role="cell">
										{{ data.maxWeight ? data.maxWeight + ' lbs' : '-' }}
									</div>
									<div class="flex-table-cell" role="cell">
										{{
											shippingCategories?.find(
												(category) => category.shippingCategoryId === data.shippingCategoryId
											)?.category
										}}
									</div>
									<div class="flex-table-cell" role="cell">&nbsp;</div>
									<div class="flex-table-cell" role="cell">
										<v-btn icon color="primary" @click="activeEdit = data"
											><v-icon small>mdi-pencil</v-icon>
										</v-btn>
									</div>
								</template>
							</div>
						</div>
					</div>
					<div class="table-controls">
						<v-btn depressed small :disabled="!hasPreviousPage" @click="pageIndexOffset -= 15">
							Previous
						</v-btn>
						<v-btn depressed small :disabled="!hasNextPage" @click="pageIndexOffset += 15">
							Next Page
						</v-btn>
					</div>
				</v-col>
				<v-col cols="5" class="flex-auto">
					<div class="shipping-chart">
						<div class="flex-table" role="table-head">
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell" role="column-header">Shipping Calculation Code</div>
							</div>
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell" role="column-header">Order Price Ranges</div>
								<div class="flex-table-cell" role="column-header">Ground</div>
								<div class="flex-table-cell" role="column-header">3-Day</div>
								<div class="flex-table-cell" role="column-header">2-Day</div>
								<div class="flex-table-cell" role="column-header">Overnight</div>
								<div class="flex-table-cell" role="column-header">&nbsp;</div>
							</div>
						</div>
						<div class="flex-table" role="table-body">
							<div
								v-for="(data, i) in shippingMethodCalculations"
								:key="i"
								class="flex-table-row"
								role="row-group"
							>
								<template v-if="activeEdit == data">
									<div class="flex-table-cell-edit" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.rangeStart"
												label="Range Start"
												disabled
												:rules="[numberRule]"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell-edit" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.rangeEnd"
												label="Range End"
												disabled
												:rules="[numberRule]"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell-edit" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.ground"
												label="Ground"
												:rules="[numberRule]"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell-edit" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.threeDay"
												label="3-Day"
												:rules="[numberRule]"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell-edit" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.twoDay"
												label="2-Day"
												:rules="[numberRule]"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell-edit" role="cell">
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.overnight"
												label="Overnight"
												:rules="[numberRule]"
											></v-text-field>
										</div>
									</div>
									<div class="flex-table-cell-edit" role="cell">
										<v-btn
											icon
											color="primary"
											@click="$apollo.queries.shippingMethodCalculations.refetch()"
											><v-icon small>mdi-close</v-icon>
										</v-btn>
									</div>
									<div class="flex-table-cell-edit" role="cell">
										<v-btn icon color="primary" @click="saveShippingMethodCalculation()">
											<v-icon small>mdi-check</v-icon>
										</v-btn>
									</div>
								</template>
								<template v-else>
									<div class="flex-table-cell" role="cell">
										{{ renderPrice(data.rangeStart) }} - {{ data.rangeEnd >= 99999999 ? '$10,000+' : renderPrice(data.rangeEnd) }}
									</div>
									<div class="flex-table-cell" role="cell">{{ renderPrice(data.ground) }}</div>
									<div class="flex-table-cell" role="cell">{{ renderPrice(data.threeDay) }}</div>
									<div class="flex-table-cell" role="cell">{{ renderPrice(data.twoDay) }}</div>
									<div class="flex-table-cell" role="cell">{{ renderPrice(data.overnight) }}</div>
									<div class="flex-table-cell" role="cell">
										<v-btn icon color="primary" @click="activeEdit = data"
											><v-icon small>mdi-pencil</v-icon>
										</v-btn>
									</div>
								</template>
							</div>
						</div>
					</div>
					<div class="table-controls">
						<v-btn
							depressed
							small
							:disabled="!calcHasPreviousPage"
							@click="calcPageIndexOffset -= 15"
						>
							Previous
						</v-btn>
						<v-btn depressed small :disabled="!calcHasNextPage" @click="calcPageIndexOffset += 15">
							Next Page
						</v-btn>
					</div>
				</v-col>
			</v-row>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete the selected shipping methods?'"
			:itemList="deleteMethods"
			:itemListKey="'shippingMethodName'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deleteShippingMethod();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import { renderPrice } from "@/helpers/PricingCalculator";
import { getApiUrl } from "@/helpers";
import UpsLogo from "@/assets/svg-icons/ups-logo.svg";
import FedexLogo from "@/assets/svg-icons/fedex-logo.svg";
import UspsLogo from "@/assets/svg-icons/usps-logo.svg";

export default {
	name: "ShippingMethod",
	components: {
		WarningModal,
	},
	data() {
		return {
			initData: false,
			shippingMethods: [],
			deleteMethods: [],
			activeEdit: null,
			carriers: [],
			carrierServices: {},
			carrierPackages: {},
			deleteWarning: false,
			pageIndexOffset: 0,
			hasNextPage: false,
			hasPreviousPage: false,
			calcPageIndexOffset: 0,
			calcHasNextPage: false,
			calcHasPreviousPage: false,
			startSort: false,
			dragItemElement: null,
			dragItem: null,
			numberMask: /^[0-9]*$/,
		};
	},
	apollo: {
		shippingMethods: {
			query() {
				return gql`
					query GetShippingMethods($pageOffset: Int!) {
						shippingMethods(skip: $pageOffset, take: 15, order: { sequence: ASC }) {
							items {
								shippingMethodId
								shippingMethodName
								shippingCategoryId
								sequence
								carrierCode
								carrierId
								serviceCode
								packageType
								maxWeight
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			variables() {
				return {
					pageOffset: this.pageIndexOffset,
				};
			},
			update(data) {
				if (this.initData) {
					this.initData = false;
				}

				this.hasNextPage = data.shippingMethods.pageInfo.hasNextPage;
				this.hasPreviousPage = data.shippingMethods.pageInfo.hasPreviousPage;

				// Backward compatibility: auto-populate carrierId from carrierCode if missing
				const methods = data.shippingMethods.items.map(method => {
					if (!method.carrierId && method.carrierCode && this.carriers.length > 0) {
						// Find the first carrier matching the carrierCode
						const matchingCarrier = this.carriers.find(c => c.carrierCode === method.carrierCode);
						if (matchingCarrier) {
							return { ...method, carrierId: matchingCarrier.carrierId };
						}
					}
					return method;
				});

				return methods;
			},
		},
		shippingMethodCalculations: {
			query() {
				return gql`
					query GetShippingMethodCalculations($pageOffset: Int!) {
						shippingMethodCalculations(skip: $pageOffset, take: 15, order: { sequence: ASC }) {
							items {
								shippingMethodCalculationId
								rangeStart
								rangeEnd
								ground
								threeDay
								twoDay
								overnight
								sequence
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			variables() {
				return {
					pageOffset: this.calcPageIndexOffset,
				};
			},
			update(data) {
				if (this.initData) {
					this.initData = false;
				}

				this.calcHasNextPage = data.shippingMethodCalculations.pageInfo.hasNextPage;
				this.calcHasPreviousPage = data.shippingMethodCalculations.pageInfo.hasPreviousPage;
				return data.shippingMethodCalculations.items;
			},
		},
		shippingCategories: {
			query() {
				return gql`
					query {
						shippingCategory {
							shippingCategoryId
							category
							sequence
						}
					}
				`;
			},
			update(data) {
				return data.shippingCategory;
			},
		},
		carriers: {
			query() {
				return gql`
					query {
						carriers {
							items {
								carrierId
								carrierCode
								friendlyName
								nickname
								accountNumber
								services {
									serviceCode
									name
									isDomestic
									isInternational
								}
								packages {
									packageCode
									name
									description
								}
							}
						}
					}
				`;
			},
			update(data) {
				if (data.carriers && data.carriers.items) {
					// Build service and package lookups by carrierId for quick access
					// (supports multiple carrier accounts with the same carrierCode)
					data.carriers.items.forEach(carrier => {
						this.carrierServices[carrier.carrierId] = carrier.services || [];
						this.carrierPackages[carrier.carrierId] = carrier.packages || [];
					});

					// Create display names that distinguish carriers with the same code
					return data.carriers.items.map(carrier => ({
						...carrier,
						displayName: this.getCarrierDisplayName(carrier)
					}));
				}
				return [];
			},
		},
	},
	methods: {
		renderPrice,

		getCarrierLogo(carrierCode) {
			const logos = {
				'ups': UpsLogo,
				'fedex': FedexLogo,
				'fedex_express': FedexLogo,
				'fedex_ground': FedexLogo,
				'fedex_walleted': FedexLogo,
				'usps': UspsLogo,
			};
			return logos[carrierCode] || null;
		},

		getCarrierIcon(carrierCode) {
			const icons = {
				'ups': 'mdi-truck-delivery',
				'fedex': 'mdi-truck-fast',
				'usps': 'mdi-email-fast',
				'dhl_express': 'mdi-airplane',
				'canada_post': 'mdi-package-variant',
			};
			return icons[carrierCode] || null;
		},

		getCarrierDisplayName(carrier) {
			// Create a display name that distinguishes carriers with the same code
			// Filter out payment card nicknames (e.g., "Visa ••••2449") which aren't useful
			const isPaymentCardNickname = (nickname) => {
				if (!nickname) return true;
				const cardPatterns = ['visa', 'mastercard', 'amex', 'discover', '••••', '****'];
				const lowerNickname = nickname.toLowerCase();
				return cardPatterns.some(pattern => lowerNickname.includes(pattern));
			};

			// Priority: meaningful nickname > account number > friendlyName alone
			if (carrier.nickname && !isPaymentCardNickname(carrier.nickname) && carrier.nickname !== carrier.friendlyName) {
				return `${carrier.friendlyName} (${carrier.nickname})`;
			}

			// For carriers with the same code, show account number to distinguish
			if (carrier.accountNumber) {
				// Show last 4-6 chars of account number for privacy
				const shortAccount = carrier.accountNumber.length > 6
					? '...' + carrier.accountNumber.slice(-6)
					: carrier.accountNumber;
				return `${carrier.friendlyName} (${shortAccount})`;
			}

			return carrier.friendlyName;
		},

		getCarrierName(carrierId, carrierCode = null) {
			// Try carrierId first
			if (carrierId) {
				const carrier = this.carriers.find(c => c.carrierId === carrierId);
				if (carrier) {
					return carrier.displayName || carrier.friendlyName;
				}
			}
			// Fallback to carrierCode for backward compatibility
			if (carrierCode) {
				const carrierByCode = this.carriers.find(c => c.carrierCode === carrierCode);
				if (carrierByCode) {
					return carrierByCode.displayName || carrierByCode.friendlyName;
				}
				return carrierCode.toUpperCase();
			}
			// Last resort: try carrierId as carrierCode
			if (carrierId) {
				const carrierByCode = this.carriers.find(c => c.carrierCode === carrierId);
				return carrierByCode ? carrierByCode.displayName || carrierByCode.friendlyName : carrierId;
			}
			return '-';
		},

		getCarrierCodeFromId(carrierId) {
			if (!carrierId) return null;
			const carrier = this.carriers.find(c => c.carrierId === carrierId);
			return carrier ? carrier.carrierCode : carrierId; // Return carrierId as fallback (might be carrierCode)
		},

		getServiceName(carrierIdOrCode, serviceCode) {
			if (!carrierIdOrCode || !serviceCode) return '-';
			// Try carrierId first, then fall back to finding by carrierCode
			let services = this.carrierServices[carrierIdOrCode] || [];
			if (services.length === 0) {
				// Try to find carrier by code and get its services
				const carrier = this.carriers.find(c => c.carrierCode === carrierIdOrCode);
				if (carrier) {
					services = this.carrierServices[carrier.carrierId] || [];
				}
			}
			const service = services.find(s => s.serviceCode === serviceCode);
			return service ? service.name : serviceCode;
		},

		getPackageName(carrierIdOrCode, packageCode) {
			if (!carrierIdOrCode || !packageCode) return '-';
			// Try carrierId first, then fall back to finding by carrierCode
			let packages = this.carrierPackages[carrierIdOrCode] || [];
			if (packages.length === 0) {
				// Try to find carrier by code and get its packages
				const carrier = this.carriers.find(c => c.carrierCode === carrierIdOrCode);
				if (carrier) {
					packages = this.carrierPackages[carrier.carrierId] || [];
				}
			}
			const pkg = packages.find(p => p.packageCode === packageCode);
			return pkg ? pkg.name : packageCode;
		},

		getCarrierServices(carrierId) {
			return this.carrierServices[carrierId] || [];
		},

		getCarrierPackages(carrierId) {
			return this.carrierPackages[carrierId] || [];
		},

		/**
		 * Returns a description for UPS services to help users understand what each service offers.
		 * Only returns descriptions for UPS carriers.
		 * @param {string} serviceCode - The carrier service code
		 * @param {string} carrierId - The carrier ID to check if it's UPS
		 * @returns {string|null} Description or null if not a UPS service
		 */
		getUpsServiceDescription(serviceCode, carrierId) {
			if (!serviceCode || !carrierId) return null;

			// Check if this is a UPS carrier
			const carrier = this.carriers.find(c => c.carrierId === carrierId);
			if (!carrier || !carrier.carrierCode?.toLowerCase().includes('ups')) {
				return null;
			}

			// Normalize the service code for lookup
			const normalizedCode = serviceCode.toLowerCase();

			// Direct lookup
			if (this.upsServiceDescriptions[normalizedCode]) {
				return this.upsServiceDescriptions[normalizedCode];
			}

			// Try partial matching for variations
			for (const [key, description] of Object.entries(this.upsServiceDescriptions)) {
				if (normalizedCode.includes(key) || key.includes(normalizedCode)) {
					return description;
				}
			}

			return null;
		},

		/**
		 * Returns a warning message for services that may have restrictions or be rejected.
		 * These services typically require special account activation or have limited availability.
		 * @param {string} serviceCode - The carrier service code
		 * @returns {string|null} Warning message or null if no warning
		 */
		getServiceWarning(serviceCode) {
			if (!serviceCode) return null;

			const code = serviceCode.toLowerCase();

			// Known restricted/problematic services with their warnings
			// Sources: ShipStation Help, ShipEngine docs, UPS 2026 Tariff
			const restrictedServices = {
				// UPS Ground Saver (formerly SurePost) - Contract-only hybrid service
				// Renamed from SurePost to Ground Saver on April 2, 2025
				'ups_ground_saver': 'UPS Ground Saver (formerly SurePost) is a CONTRACT-ONLY service requiring special UPS account activation. Also: No PO Boxes, APO/FPO, Alaska, Hawaii, Puerto Rico, or US Territories. Declared value limited to $20.',
				'ups_surepost': 'UPS SurePost (now Ground Saver) is a CONTRACT-ONLY service. May be rejected if not enabled for your account.',
				'ups_surepost_lightweight': 'UPS SurePost Lightweight is CONTRACT-ONLY with weight restrictions (under 1 lb).',
				'ups_surepost_less_than_1lb': 'UPS SurePost <1lb is CONTRACT-ONLY. Requires special account activation.',
				'ups_surepost_1lb_or_greater': 'UPS SurePost 1lb+ is CONTRACT-ONLY. Requires special account activation.',
				'ups_surepost_bound_printed_matter': 'UPS SurePost BPM is CONTRACT-ONLY and restricted to books/printed materials.',
				'ups_surepost_media': 'UPS SurePost Media is CONTRACT-ONLY and restricted to media items (books, CDs, DVDs).',

				// UPS Mail Innovations - Requires separate contract
				'ups_mail_innovations_domestic': 'UPS Mail Innovations requires a SEPARATE CONTRACT with UPS. Contact your UPS rep to enable.',
				'ups_mail_innovations_international': 'UPS Mail Innovations International requires a SEPARATE CONTRACT with UPS.',
				'ups_mail_innovations_economy': 'UPS Mail Innovations Economy requires a SEPARATE CONTRACT with UPS.',
				'ups_mail_innovations_priority': 'UPS Mail Innovations Priority requires a SEPARATE CONTRACT with UPS.',
				'ups_mail_innovations_expedited': 'UPS Mail Innovations Expedited requires a SEPARATE CONTRACT with UPS.',
				'ups_mail_innovations_first_class': 'UPS Mail Innovations First Class requires a SEPARATE CONTRACT with UPS.',

				// UPS Freight services - Require freight account
				'ups_freight': 'UPS Freight requires a separate freight account and special setup.',
				'ups_freight_ltl': 'UPS Freight LTL requires a freight account.',
				'ups_freight_ltl_guaranteed': 'UPS Freight LTL Guaranteed requires a freight account.',
				'ups_ground_freight': 'UPS Ground Freight requires freight shipping setup. For palletized shipments 150+ lbs.',
				'ups_worldwide_express_freight': 'UPS Worldwide Express Freight requires freight account and customs documentation.',
				'ups_worldwide_express_freight_midday': 'UPS Worldwide Express Freight Midday requires freight account setup.',

				// FedEx hybrid/special services
				'fedex_ground_economy': 'FedEx Ground Economy (formerly SmartPost) requires special account activation.',
				'fedex_smartpost': 'FedEx SmartPost (now Ground Economy) requires special account activation.',
				'fedex_smartpost_parcel_select': 'FedEx SmartPost Parcel Select requires special account activation.',
				'fedex_first_overnight_freight': 'FedEx First Overnight Freight requires a freight account.',
				'fedex_freight_economy': 'FedEx Freight Economy requires a freight account.',
				'fedex_freight_priority': 'FedEx Freight Priority requires a freight account.',

				// USPS services with restrictions
				'usps_media_mail': 'USPS Media Mail has STRICT content restrictions (books, CDs, DVDs only). Misuse results in postage due charges.',
				'usps_library_mail': 'USPS Library Mail is RESTRICTED to libraries and academic institutions only.',
				'usps_parcel_select_ground': 'USPS Parcel Select Ground may require volume commitments.',
				'usps_parcel_select_lightweight': 'USPS Parcel Select Lightweight has weight restrictions (under 1 lb).',

				// International services with special requirements
				'fedex_international_economy_freight': 'FedEx International Economy Freight requires freight account setup.',
				'fedex_international_priority_freight': 'FedEx International Priority Freight requires freight account setup.',
			};

			return restrictedServices[code] || null;
		},

		onCarrierChange(data) {
			// Clear service and package when carrier changes
			data.serviceCode = null;
			data.packageType = null;

			// Update carrierCode based on selected carrierId
			const selectedCarrier = this.carriers.find(c => c.carrierId === data.carrierId);
			if (selectedCarrier) {
				data.carrierCode = selectedCarrier.carrierCode;
			}

			// Set default service if available
			const services = this.getCarrierServices(data.carrierId);
			if (services.length > 0) {
				// Try to find a ground service as default
				const groundService = services.find(s =>
					s.name.toLowerCase().includes('ground') ||
					s.serviceCode.toLowerCase().includes('ground')
				);
				if (groundService) {
					data.serviceCode = groundService.serviceCode;
				}
			}

			// Set default package if available
			const packages = this.getCarrierPackages(data.carrierId);
			if (packages.length > 0) {
				// Try to find a standard package as default
				const defaultPackage = packages.find(p =>
					p.name.toLowerCase().includes('package') ||
					p.name.toLowerCase().includes('box')
				) || packages[0];
				if (defaultPackage) {
					data.packageType = defaultPackage.packageCode;
				}
			}
		},

		newShippingMethod() {
			const newShippingMethod = {
				shippingMethodId: null,
				shippingMethodName: "",
				sequence: 0,
			};

			this.shippingMethods.unshift(newShippingMethod);

			//set the new shipment as the active edit
			this.activeEdit = newShippingMethod;
		},

		removeShippingMethod() {
			this.shippingMethods.splice(0, 1);
			this.activeEdit = null;
		},

		selectShippingMethods(data) {
			//add or remove the selected shipping method to the delete list
			if (this.deleteMethods.some((obj) => obj == data)) {
				this.deleteMethods = this.deleteMethods.filter((obj) => obj !== data);
			} else {
				this.deleteMethods.push(data);
			}
		},

		async deleteShippingMethod() {
			try {
				//loop through all deleteMethods and delete them by id
				for await (const method of this.deleteMethods) {
					const res = await this.$http.delete(
						getApiUrl(`/api/settings/shipping-method/${method.shippingMethodId}`)
					);
					console.log(res);
				}

				console.log("refreshing");

				//refresh latest shipping methods and reset deleteMethods
				this.$apollo.queries.shippingMethods.refetch();
				this.deleteMethods = [];
			} catch (error) {
				console.log(error);
			}
		},

		async saveShippingMethod() {
			try {
				// Prepare the data, converting empty strings to null for nullable fields
				const dataToSave = {
					...this.activeEdit,
					maxWeight: this.activeEdit.maxWeight === '' || this.activeEdit.maxWeight === null 
						? null 
						: Number(this.activeEdit.maxWeight)
				};

				let res;
				if (this.activeEdit.shippingMethodId !== null) {
					res = await this.$http.put(
						getApiUrl("/api/settings/shipping-method"),
						dataToSave
					);
				} else {
					res = await this.$http.post(
						getApiUrl("/api/settings/shipping-method"),
						dataToSave
					);
				}
				console.log(res);

				//update sequence list for newest items
				await this.updateSequenceList(res.data.shippingMethodId);

				//refresh latest methods and clear activeEdit
				this.$apollo.queries.shippingMethods.refetch();
				this.activeEdit = null;
			} catch (error) {
				console.log(error);
			}
		},

		async saveShippingMethodCalculation() {
			try {
				let res = await this.$http.put(
					getApiUrl("/api/settings/shipping-method-calc"),
					this.activeEdit
				);
				console.log(res);

				//refresh latest methods and clear activeEdit
				this.$apollo.queries.shippingMethodCalculations.refetch();
				this.activeEdit = null;
			} catch (error) {
				console.log(error);
			}
		},

		async updateSequenceList(id) {
			try {
				//create list of sequeneces and ids
				let sequenceList = this.shippingMethods.map((obj) => {
					let sequenceObj = {
						//if no id is defined then use the passed in id (that means the user created a new entry and this needs to be added to the sequence list)
						entityId: obj.shippingMethodId ? obj.shippingMethodId : id,
						//if user is using paging then we need to add the index offset to the sequence count
						sequence:
							this.pageIndexOffset !== 0 ? obj.sequence + this.pageIndexOffset : obj.sequence,
					};
					return sequenceObj;
				});

				const res = await this.$http.put(
					getApiUrl("/api/settings/shipping-methods/sequence"),
					{ sequence: sequenceList }
				);
				console.log(res);

				return;
			} catch (error) {
				console.log(error);
			}
		},

		//drag and drop sorting functions
		onDrop() {
			//if sorting is active reset dragging states after dropping the dragged item
			if (this.dragItem) {
				this.startSort = false;
				this.dragItemElement.classList.remove("dragging");
				this.dragItemElement = null;
				this.dragItem = null;
			}
		},

		renderItemOrder(item) {
			//find the matching item in sequence list
			const matchingSortItem = this.sequenceList.find(
				(obj) => obj.shippingMethodId == item.shippingMethodId
			);

			return matchingSortItem ? matchingSortItem.sequence : 0;
		},

		triggerSorting(event, item) {
			this.startSort = true;
			//set dragging element in data and add dragging class (this is removed on drag drop event)
			this.dragItemElement = event.target.closest(".flex-table-row");
			this.dragItemElement.classList.add("dragging");

			//set drag item in data
			this.dragItem = item;
		},

		setItemOrder(closestItem) {
			let sortedList = [];

			//get all items except the current drag item and sort them in order
			sortedList = this.shippingMethods
				.filter((obj) => obj !== this.dragItem)
				.sort((a, b) => a.sequence - b.sequence);

			if (closestItem) {
				//find the closestItem in data by DOM style order property
				let closestSortItemIndex = sortedList.findIndex(
					(obj) => obj.sequence == closestItem.style.order
				);

				//assign dragItem above the passed in closestItem
				sortedList.splice(closestSortItemIndex, 0, this.dragItem);
			} else {
				//assign the selected item to the end of its level
				sortedList.push(this.dragItem);
			}

			//set the new sequence order via index + 1
			sortedList = sortedList.map((obj, i) => {
				return { ...obj, sequence: i + 1 };
			});

			//update the shipping list with new sequences from the sortedList
			sortedList.forEach((item) => {
				let foundIndex = this.shippingMethods.findIndex(
					(obj) => obj.shippingMethodId == item.shippingMethodId
				);
				this.shippingMethods[foundIndex].sequence = item.sequence;
			});
		},

		sortItems(event) {
			//get all item elements that are not the dragged item
			const nonActiveItemElements = [
				...event.target.closest(".flex-table").querySelectorAll(".flex-table-row:not(.dragging)"),
			];

			//find the closest item that were above in the DOM
			let closestItem = nonActiveItemElements.reduce(
				(closest, child) => {
					const box = child.getBoundingClientRect();
					const offset = event.clientY - box.top - box.height / 2;
					if (offset < 0 && offset > closest.offset) {
						return { offset: offset, element: child };
					} else {
						return closest;
					}
				},
				{ offset: Number.NEGATIVE_INFINITY }
			).element;

			//change the item order in data by element names
			if (closestItem == null) {
				this.setItemOrder();
			} else {
				this.setItemOrder(closestItem);
			}
		},

		// Backward compatibility: populate carrierId from carrierCode for existing shipping methods
		populateMissingCarrierIds() {
			if (!this.shippingMethods || !this.carriers || this.carriers.length === 0) return;

			this.shippingMethods.forEach((method, index) => {
				if (!method.carrierId && method.carrierCode) {
					// Find the first carrier matching the carrierCode
					const matchingCarrier = this.carriers.find(c => c.carrierCode === method.carrierCode);
					if (matchingCarrier) {
						// Use Vue.set for reactivity
						this.$set(this.shippingMethods[index], 'carrierId', matchingCarrier.carrierId);
					}
				}
			});
		},
	},
	mounted() {
		this.initData = true;
	},
	watch: {
		// When carriers load, update shipping methods that are missing carrierId
		carriers: {
			handler(newCarriers) {
				if (newCarriers && newCarriers.length > 0 && this.shippingMethods && this.shippingMethods.length > 0) {
					this.populateMissingCarrierIds();
				}
			},
			immediate: false
		},
		// When shipping methods load, check if carriers are available to populate carrierId
		shippingMethods: {
			handler(newMethods) {
				if (newMethods && newMethods.length > 0 && this.carriers && this.carriers.length > 0) {
					this.populateMissingCarrierIds();
				}
			},
			immediate: false
		}
	},
	computed: {
		numberRule() {
			return (value) => /^-?\d*(\.\d+)?$/.test(value) || "Only numbers are allowed";
		},
		// UPS Service descriptions lookup - maps service codes to their descriptions
		upsServiceDescriptions() {
			return {
				// Domestic US Services
				'ups_next_day_air_early': 'Guaranteed early-morning, next-business-day delivery from most US 48 addresses to most US 48 and limited Alaska addresses.',
				'ups_next_day_air_early_am': 'Guaranteed early-morning, next-business-day delivery from most US 48 addresses to most US 48 and limited Alaska addresses.',
				'ups_next_day_air': 'Guaranteed next-business-day delivery from and to most US 50 and Puerto Rico addresses, with some limitations in Alaska and Hawaii.',
				'ups_next_day_air_saver': 'Guaranteed next-business-day delivery from most US 48 addresses to most US 48 addresses.',
				'ups_2nd_day_air_am': 'Guaranteed morning second-business-day delivery from most commercial US 48 addresses to most commercial US 48 destinations.',
				'ups_2nd_day_air': 'Guaranteed second-business-day delivery from and to most US 50 and Puerto Rico addresses, with some limitations in Alaska and Hawaii.',
				'ups_3_day_select': 'Guaranteed three-working-day delivery from and to most US 48 addresses.',
				'ups_ground': 'Guaranteed day-definite delivery for commercial and residential shipments in 1-5 days from and to most US 50 and Puerto Rico addresses.',

				// International Services
				'ups_worldwide_express_plus': 'Guaranteed early-morning, day-definite international delivery from 65+ countries to 30 countries including major cities in Canada, Europe, and Asia.',
				'ups_worldwide_express': 'Guaranteed morning delivery in 1-3 days for international shipments from 185+ countries to 45 countries.',
				'ups_worldwide_saver': 'Guaranteed afternoon delivery in 1-3 days for international shipments from 185+ countries to 215 countries.',
				'ups_worldwide_expedited': 'Guaranteed economical delivery for less urgent international shipments from and to 60+ countries.',
				'ups_standard': 'DOMESTIC: Economical ground delivery for less-urgent shipments within the US and to Canada. Day-definite delivery in 5+ days.',
				'ups_standard_international': 'INTERNATIONAL: Economical delivery for less-urgent cross-border shipments. Day-definite delivery within 5 days to 30+ countries.',
				'ups_ground_international': 'INTERNATIONAL: Ground shipping for cross-border shipments. Economical option for less time-sensitive international deliveries.',
				'ups_worldwide_economy_ddp': 'Low-cost deferred international service for low-weight (<4.5 kg), low-value shipments. Duties and taxes paid by shipper.',
				'ups_worldwide_economy_ddu': 'Low-cost deferred international service for low-weight (max 2 kg), low-value shipments. Duties and taxes collected at delivery.',
				'ups_economy_ddp': 'Low-cost deferred international service. Duties and taxes paid by shipper at time of shipment (Delivered Duties Paid).',
				'ups_economy_ddu': 'Low-cost deferred international service. Duties and taxes unpaid, collected at delivery (Delivered Duties Unpaid).',

				// Express Services (International variants)
				'ups_express': 'Guaranteed next-business-day delivery to most US business areas or day-definite delivery to Canada, Mexico, Central and South America.',
				'ups_express_plus': 'Guaranteed earliest morning delivery. Premium express service for time-critical shipments.',
				'ups_express_noon': 'Express service with a guaranteed delivery commit time of 12:00 noon.',
				'ups_express_1200': 'Express service with a guaranteed delivery commit time of 12:00 noon.',
				'ups_express_early_am': 'Express Early A.M. service for shipments to the U.S. Earliest delivery commitment.',
				'ups_express_na1': 'UPS Express (NA1): Next-business-day to US or day-definite to Canada, Mexico, Central and South America.',
				'ups_next_day_air_international': 'INTERNATIONAL: Next Day Air service for cross-border shipments requiring next-day delivery.',
				'ups_2nd_day_air_international': 'INTERNATIONAL: 2nd Day Air service for cross-border shipments requiring second-day delivery.',
				'ups_access_point_economy': 'Economical service for Hold at UPS Access Point or Return Services. Requires UPS Access Point Economy contract.',

				// Ground Saver / SurePost (contract-only hybrid services)
				'ups_ground_saver': 'CONTRACT-ONLY hybrid service using USPS for final delivery. For lightweight residential shipments. No PO Boxes, APO/FPO, AK, HI, PR.',
				'ups_surepost': 'CONTRACT-ONLY hybrid service (now called Ground Saver). Uses USPS for final mile delivery.',
				'ups_surepost_lightweight': 'CONTRACT-ONLY for packages under 1 lb. Uses USPS for final delivery.',
				'ups_surepost_less_than_1lb': 'CONTRACT-ONLY for packages under 1 lb. Uses USPS for final delivery.',
				'ups_surepost_1lb_or_greater': 'CONTRACT-ONLY for packages 1 lb or more. Uses USPS for final delivery.',

				// Mail Innovations (separate contract required)
				'ups_mail_innovations_domestic': 'SEPARATE CONTRACT REQUIRED. Consolidation service using USPS for final delivery.',
				'ups_mail_innovations_international': 'SEPARATE CONTRACT REQUIRED. International mail service.',
				'ups_mail_innovations_economy': 'SEPARATE CONTRACT REQUIRED. Economy mail consolidation service.',
				'ups_mail_innovations_priority': 'SEPARATE CONTRACT REQUIRED. Priority mail consolidation service.',
				'ups_mail_innovations_first_class': 'SEPARATE CONTRACT REQUIRED. First class mail consolidation service.',
			};
		},
	},
};
</script>

<style lang="scss" scoped>
.shipping {
	&-header {
		&-actions {
			grid-column: 3;
			margin-left: auto;

			.v-btn {
				min-width: 13rem;
				text-transform: initial;

				&:first-child {
					background-color: $color-white;
					border-color: $color-gray;
				}
			}
		}
	}

	&-table {
		position: relative;
		height: 0;
		overflow-y: auto;
		overflow-x: auto;
		flex-grow: 1;
		background-color: $color-white;
		border-radius: 3px;

		.form-input {
			width: 100%;
			margin: 0;
			padding: 0 4px;
		}

		.flex-table {
			border: none;

			&[role="table-head"] {
				position: sticky;
				top: 0;
				z-index: 1;
				background-color: $color-white;
				
				.flex-table-cell {
					font-weight: 600;
					color: $color-gray-dark;
				}
			}

			&[role="table-body"] {
				.flex-table-row {
					border-top: none;
					border-bottom: solid 1px $color-gray;
					
					&:hover {
						background-color: rgba($color-gray, 0.05);
					}
				}
			}
		}

		.flex-table-cell {
			padding: 0.6rem 0.4rem;
			text-overflow: ellipsis;
			overflow: hidden;
			white-space: nowrap;
			
			&:nth-child(1) {
				width: 4%;
				justify-content: center;
				padding: 0.6rem 0.2rem;
			}
			&:nth-child(2) {
				width: 16%;
			}
			&:nth-child(3) {
				width: 11%;
			}
			&:nth-child(4) {
				width: 17%;
			}
			&:nth-child(5) {
				width: 17%;
			}
			&:nth-child(6) {
				width: 9%;
				text-align: center;
			}
			&:nth-child(7) {
				width: 16%;
			}
			&:nth-child(8) {
				width: 5%;
				justify-content: center;
				padding: 0.6rem 0.2rem;
			}
			&:nth-child(9) {
				width: 5%;
				justify-content: center;
				padding: 0.6rem 0.2rem;
			}
		}

		.carrier-cell {
			display: flex;
			align-items: center;
		}

		.carrier-logo {
			height: 20px;
			width: auto;
			max-width: 60px;
			margin-right: 8px;
			object-fit: contain;
		}

		.carrier-logo-dropdown {
			height: 16px;
			width: auto;
			max-width: 40px;
			display: inline-block;
			vertical-align: middle;
			object-fit: contain;
		}
	}

	&-chart {
		position: relative;
		background-color: $color-white;
		border-radius: 3px;
		overflow: hidden;

		.flex-table {
			border: none;

			&[role="table-head"] {
				.flex-table-row {
					border-bottom: 1px solid $color-black;

					&:first-child {
						.flex-table-cell {
							width: 100%;
							justify-content: flex-start;
						}
					}
					// &:nth-child(2) {
					//     .flex-table-cell:first-child {
					//        border-right: 1px solid $color-black;
					//     }
					// }
				}
			}

			&[role="table-body"] {
				.flex-table-row {
					border-top: none;

					&:not(:last-child) {
						border-bottom: solid 1px $color-gray;
					}
				}
			}
		}

		.flex-table-cell {
			&:nth-child(1) {
				width: 22%;
				justify-content: center;
				// border-right: 1px solid $color-gray-2;
			}
			&:nth-child(2) {
				width: 18%;
				justify-content: center;
			}
			&:nth-child(3) {
				width: 18%;
				justify-content: center;
			}
			&:nth-child(4) {
				width: 18%;
				justify-content: center;
			}
			&:nth-child(5) {
				width: 18%;
				justify-content: center;
			}
			&:nth-child(6) {
				width: 6%;
				justify-content: center;
			}
		}

		.flex-table-cell-edit {
			display: flex;
			align-items: center;
			padding: 0.8rem;

			&:nth-child(1) {
				width: 15%;
				justify-content: center;
				// border-right: 1px solid $color-gray-2;
			}
			&:nth-child(2) {
				width: 15%;
				justify-content: center;
			}
			&:nth-child(3) {
				width: 15%;
				justify-content: center;
			}
			&:nth-child(4) {
				width: 15%;
				justify-content: center;
			}
			&:nth-child(5) {
				width: 15%;
				justify-content: center;
			}
			&:nth-child(6) {
				width: 15%;
				justify-content: center;
			}
			&:nth-child(7) {
				width: 5%;
				justify-content: center;
			}
			&:nth-child(8) {
				width: 5%;
				justify-content: center;
			}
		}
	}
}

</style>

<style lang="scss">
// UPS Service Tooltip Styles (unscoped for Vuetify tooltip portal)
.ups-service-tooltip {
	background-color: #1a237e !important;
	padding: 12px 16px !important;
	border-radius: 8px !important;

	.ups-tooltip-content {
		.ups-tooltip-title {
			font-weight: 600;
			font-size: 13px;
			margin-bottom: 6px;
			color: #fff;
			border-bottom: 1px solid rgba(255, 255, 255, 0.2);
			padding-bottom: 6px;
		}

		.ups-tooltip-desc {
			font-size: 12px;
			line-height: 1.5;
			color: rgba(255, 255, 255, 0.95);
		}
	}
}
</style>
